import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { Input, version2 } from 'ui-components/components/input';
import { DropdownField } from 'ui-components/components/dropdown/dropdown-field';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';

import type { ValidationError } from '../../../../validation/validation-error';
import { AddressFields } from '../../../../address/address-fields';
import { useSortedTranslatedCountries } from '../../../../hooks/use-sorted-translated-countries';
import { US } from '../../../../constants/country-codes';
import { messages } from './messages';
import type { Address } from '../../types';

type BillingAddressFormData = {
  fullName: string;
  companyName: string;
  address: Address;
};

type BillingAddressFormProps = {
  data: BillingAddressFormData;
  errors?: Record<string, ValidationError>;
  isDisabled?: boolean;
  showAllErrors?: boolean;
  onChange: (field: string, value: string | Address) => void;
  onBlur: (field: string) => void;
};

export const BillingAddressForm = ({
  data,
  errors,
  isDisabled = false,
  showAllErrors = false,
  onChange,
  onBlur,
}: BillingAddressFormProps) => {
  const intl = useIntl();
  const countries = useSortedTranslatedCountries({ labelProp: 'name' });
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});

  // Convert countries to dropdown options format
  const countryOptions = useMemo(
    () =>
      (countries as Array<{ code: string; name: string }>).map((country) => ({
        value: country.code,
        label: country.name,
      })),
    [countries],
  );

  const getFieldError = useCallback(
    (field: string) => {
      const shouldShowError = showAllErrors || touchedFields[field];
      const error = shouldShowError ? errors?.[field] : null;
      const inlineMessage = error?.message;
      const inlineMessageType = inlineMessage ? INLINE_MESSAGE_TYPES.error : undefined;
      return { inlineMessage, inlineMessageType };
    },
    [errors, showAllErrors, touchedFields],
  );

  const handleFieldChange = useCallback(
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(field, event.target.value);
    },
    [onChange],
  );

  const handleFieldBlur = useCallback(
    (field: string) => () => {
      setTouchedFields((prev) => ({ ...prev, [field]: true }));
      onBlur(field);
    },
    [onBlur],
  );

  const handleAddressFieldChange = useCallback(
    (fieldName: string, value: string) => {
      onChange('address', {
        ...data.address,
        [fieldName]: value,
      });
    },
    [data.address, onChange],
  );

  const handleAddressBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      const target = event.target as HTMLInputElement;
      if (target.name) {
        onBlur(`address.${target.name}`);
      }
    },
    [onBlur],
  );

  return (
    <FlexColumn gap={Gap.Space16}>
      <DropdownField
        isDisabled={isDisabled}
        label={intl.formatMessage(messages.countryLabel)}
        onItemSelected={(countryCode) => handleAddressFieldChange('countryCode', countryCode)}
        options={countryOptions}
        selectedItemValue={data.address.countryCode || US}
      />

      <Input
        ariaLabel={intl.formatMessage(messages.fullNameLabel)}
        disabled={isDisabled}
        inlineMessage={getFieldError('billingFullName').inlineMessage}
        inlineMessageType={getFieldError('billingFullName').inlineMessageType}
        label={intl.formatMessage(messages.fullNameLabel)}
        onBlur={handleFieldBlur('billingFullName')}
        onChange={handleFieldChange('fullName')}
        value={data.fullName}
        version={version2}
      />

      <Input
        ariaLabel={intl.formatMessage(messages.companyNameLabel)}
        disabled={isDisabled}
        inlineMessage={getFieldError('billingCompanyName').inlineMessage}
        inlineMessageType={getFieldError('billingCompanyName').inlineMessageType}
        label={intl.formatMessage(messages.companyNameLabelOptional)}
        onBlur={handleFieldBlur('billingCompanyName')}
        onChange={handleFieldChange('companyName')}
        value={data.companyName}
        version={version2}
      />

      <div onBlur={handleAddressBlur}>
        <AddressFields
          address={data.address}
          disabled={isDisabled}
          errors={errors}
          hiddenFields={['name', 'company', 'phone', 'email', 'countryCode']}
          isFormDescendant
          onChange={handleAddressFieldChange}
          showAllErrors={showAllErrors}
          type='v2'
        />
      </div>
    </FlexColumn>
  );
};
